import type { QuickstartTheme } from '../types/theme';

/**
 * Peer terminal phases — mutually exclusive end states for a Quickstart visit.
 *
 * Adding a new end phase means extending this union and teaching
 * `resolveTerminalPhase` when to pick it. Coordinators should transition to
 * whatever phase the resolver returns rather than hard-coding sibling branches.
 *
 * - `device_not_supported` — entry gate: this device cannot play; handoff UI
 * - `thank_you` — session/CTA end: designed thank-you stage (default)
 * - `auto_redirect` — session/CTA end: bounce to the store URL
 */
export type TerminalPhase = 'device_not_supported' | 'thank_you' | 'auto_redirect';

/**
 * Inputs for choosing a peer terminal phase.
 *
 * `deviceGate` is only set on the entry device-lock check. When the visitor is
 * unsupported and the fallback screen is enabled, we land on
 * `device_not_supported`. When they are unsupported but the campaign opted that
 * class out of the screen, we fall through to the same thank-you / auto-redirect
 * peers as a normal session exit (the historical CTA escape hatch).
 *
 * Session exits omit `deviceGate` and only choose between thank-you and auto-redirect.
 */
export type TerminalResolveInput = {
  theme: Pick<QuickstartTheme, 'autoRedirectToCTA'> | undefined;
  hasResolvedCtaUrl: boolean;
  deviceGate?: {
    supported: boolean;
    fallbackEnabled: boolean;
  };
};

/**
 * Whether an end-of-session (or CTA-escape) exit should bounce to the store.
 *
 * Rule: auto-redirect ONLY when the campaign explicitly opts in
 * (`theme.autoRedirectToCTA === true`) AND a CTA URL is configured.
 * Everything else is the thank-you page (the safe default).
 */
export const shouldAutoRedirectToStore = (
  theme: Pick<QuickstartTheme, 'autoRedirectToCTA'> | undefined,
  hasResolvedCtaUrl: boolean,
): boolean => theme?.autoRedirectToCTA === true && hasResolvedCtaUrl;

/**
 * Single source of truth for which peer terminal phase a visit should land on.
 *
 * Precedence:
 * 1. Entry device gate → `device_not_supported` when blocked and fallback on
 * 2. Else thank-you vs auto-redirect via `shouldAutoRedirectToStore`
 */
export const resolveTerminalPhase = (input: TerminalResolveInput): TerminalPhase => {
  const { theme, hasResolvedCtaUrl, deviceGate } = input;

  if (deviceGate && !deviceGate.supported && deviceGate.fallbackEnabled) {
    return 'device_not_supported';
  }

  return shouldAutoRedirectToStore(theme, hasResolvedCtaUrl) ? 'auto_redirect' : 'thank_you';
};
